// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";

// Utils
import { num } from "../../shared/formatters";

type NearPointChartType = {
  breakCentimetres: number | null;
  recoveryCentimetres: number | null;
  reachedTheNose: boolean;
} | null;

const W = 600;
const H = 74;
const L = 24;
const R = 24;
// Centered vertically in H: a break label sits above the axis, the tick labels sit below it, and
// a recovery label sits below those -- AXIS_Y is picked so that stack lands with even top/bottom
// margins rather than pooling toward the bottom of the viewBox.
const AXIS_Y = 30;
const BAR_Y = 24;
const BAR_H = 12;
const RECOVERY_LABEL_Y = 64;

const BREAK_COLOR = "#E2772E";
const RECOVERY_COLOR = "#202338";

// A ruler from the nose out to the farthest of break/recovery (or 50 cm, whichever is greater),
// with the diplopia span shaded between the break and recovery points.
const NearPointChart = ({ nearPoint }: { nearPoint: NearPointChartType }) => {
  const { t } = useTranslation();

  const geometry = useMemo(() => {
    const far =
      Math.ceil(
        Math.max(
          50,
          nearPoint?.breakCentimetres ?? 0,
          nearPoint?.recoveryCentimetres ?? 0
        ) / 10
      ) * 10;
    const track = W - L - R;
    const sx = (cm: number) => L + (cm / far) * track;
    const ticks: Array<number> = [];
    for (let cm = 0; cm <= far; cm += 10) ticks.push(cm);
    return { sx, ticks };
  }, [nearPoint?.breakCentimetres, nearPoint?.recoveryCentimetres]);

  if (!nearPoint) return null;

  const { sx, ticks } = geometry;
  const hasBreak = nearPoint.breakCentimetres !== null;
  const hasRecovery = nearPoint.recoveryCentimetres !== null;

  return (
    <Box sx={{ borderBottom: "1px solid #5B6D7A40", mb: "8px" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t("Near point")}
        style={{ width: "100%", height: "auto" }}
      >
        <line x1={L} y1={AXIS_Y} x2={W - R} y2={AXIS_Y} stroke="#5B6D7A80" />
        {ticks.map((cm) => (
          <g key={cm}>
            <line
              x1={sx(cm)}
              y1={AXIS_Y}
              x2={sx(cm)}
              y2={AXIS_Y + 5}
              stroke="#5B6D7A80"
            />
            <text
              x={sx(cm)}
              y={AXIS_Y + 18}
              fontSize="10"
              fill="#5B6D7A"
              textAnchor="middle"
            >
              {cm === 0 ? t("nose") : `${cm} cm`}
            </text>
          </g>
        ))}

        {hasBreak && hasRecovery && (
          <rect
            x={Math.min(
              sx(nearPoint.breakCentimetres as number),
              sx(nearPoint.recoveryCentimetres as number)
            )}
            y={BAR_Y}
            width={Math.abs(
              sx(nearPoint.recoveryCentimetres as number) -
                sx(nearPoint.breakCentimetres as number)
            )}
            height={BAR_H}
            rx={2}
            fill={BREAK_COLOR}
            opacity={0.25}
          />
        )}

        {hasBreak && (
          <>
            <circle
              cx={sx(nearPoint.breakCentimetres as number)}
              cy={AXIS_Y}
              r={5}
              fill={BREAK_COLOR}
            />
            <text
              x={sx(nearPoint.breakCentimetres as number)}
              y={BAR_Y - 6}
              fontSize="11"
              fontWeight={600}
              fill="#202338"
              textAnchor="middle"
            >
              {t("break")} {num(nearPoint.breakCentimetres)}
            </text>
          </>
        )}

        {hasRecovery && (
          <>
            <circle
              cx={sx(nearPoint.recoveryCentimetres as number)}
              cy={AXIS_Y}
              r={5}
              fill={RECOVERY_COLOR}
            />
            <text
              x={sx(nearPoint.recoveryCentimetres as number)}
              y={RECOVERY_LABEL_Y}
              fontSize="11"
              fontWeight={600}
              fill="#202338"
              textAnchor="middle"
            >
              {t("recovery")} {num(nearPoint.recoveryCentimetres)}
            </text>
          </>
        )}

        {!hasBreak && nearPoint.reachedTheNose && (
          <>
            <circle cx={sx(0)} cy={AXIS_Y} r={5} fill={RECOVERY_COLOR} />
            <text
              x={sx(0)}
              y={BAR_Y - 6}
              fontSize="11"
              fontWeight={600}
              fill="#202338"
              textAnchor="start"
            >
              {t("reached the nose")}
            </text>
          </>
        )}
      </svg>

      <Box display="flex" flexWrap="wrap" columnGap="16px" sx={{ mb: "8px" }}>
        <Box
          component="span"
          sx={{
            fontSize: "12px",
            color: "#5B6D7A",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            component="span"
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: BREAK_COLOR,
            }}
          />
          {t("break")}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "12px",
            color: "#5B6D7A",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            component="span"
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: RECOVERY_COLOR,
            }}
          />
          {t("recovery")}
        </Box>
        <Box component="span" sx={{ fontSize: "12px", color: "#5B6D7A" }}>
          {t("the shaded span is where the target is seen double")}
        </Box>
      </Box>
    </Box>
  );
};

export default NearPointChart;
